/**
 * WaitlistModal.js — In-app Pro waitlist modal
 */
import React, { useState } from 'react';
import { submitWaitlistEntry, SALARY_BRACKETS, MONEY_WORRIES } from '../utils/waitlistService';
import { useModalA11y } from '../utils/useModalA11y';

function WaitlistModal({ source = 'in-app', featureTitle = 'Pro Feature', onClose }) {
    const [email, setEmail] = useState('');
    const [salaryBracket, setSalaryBracket] = useState('');
    const [moneyWorry, setMoneyWorry] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    useModalA11y({ onClose, isOpen: true });

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = email.trim() && emailRegex.test(email.trim()) && salaryBracket && moneyWorry;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) { setError('Email is required.'); return; }
        if (!emailRegex.test(email.trim())) { setError('Please enter a valid email.'); return; }
        if (!salaryBracket) { setError('Please select your salary range.'); return; }
        if (!moneyWorry) { setError('Please select your biggest money worry.'); return; }

        setSubmitting(true);
        try {
            await submitWaitlistEntry({ email, salaryBracket, moneyWorry, source });
            setSubmitted(true);
        } catch {
            setError('Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    return (
        <div
            className="sp-modal-overlay"
            style={{ zIndex: 1200 }}
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="sp-modal waitlist-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="waitlist-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="sp-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>

                {submitted ? (
                    <div className="waitlist-success">
                        <div className="waitlist-success-icon" aria-hidden="true">✓</div>
                        <h3 id="waitlist-modal-title">You&apos;re on the list!</h3>
                        <p className="sp-modal-message">
                            Early waitlist members get <strong>3 months of Pro free</strong>. We&apos;ll email you when it launches.
                        </p>
                        <button type="button" className="sp-btn-primary" onClick={onClose}>Continue</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="waitlist-form">
                        <div className="waitlist-form-head">
                            <div className="waitlist-pro-row">
                                <span className="waitlist-pro-badge">PRO</span>
                                <span className="waitlist-feature">{featureTitle}</span>
                            </div>
                            <h3 id="waitlist-modal-title">Join the waitlist</h3>
                            <p className="sp-modal-message">
                                Get <strong>6 months free</strong> when Pro launches. Early access only.
                            </p>
                        </div>

                        <div className="sp-field">
                            <label className="sp-field-label" htmlFor="waitlist-email">Email address</label>
                            <input
                                id="waitlist-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                autoComplete="email"
                                className="sp-input"
                                inputMode="email"
                            />
                        </div>

                        <div className="sp-field">
                            <label className="sp-field-label" htmlFor="waitlist-salary">Approx. monthly salary</label>
                            <select
                                id="waitlist-salary"
                                value={salaryBracket}
                                onChange={(e) => setSalaryBracket(e.target.value)}
                                className="sp-input sp-select"
                            >
                                {SALARY_BRACKETS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="sp-field">
                            <label className="sp-field-label" htmlFor="waitlist-worry">Biggest money worry</label>
                            <select
                                id="waitlist-worry"
                                value={moneyWorry}
                                onChange={(e) => setMoneyWorry(e.target.value)}
                                className="sp-input sp-select"
                            >
                                {MONEY_WORRIES.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>

                        {error && <div className="sp-form-error" role="alert">{error}</div>}

                        <button
                            type="submit"
                            className="sp-btn-primary"
                            disabled={submitting || !isValid}
                        >
                            {submitting ? 'Joining...' : 'Join Early Access — 3 months free'}
                        </button>

                        <p className="waitlist-fineprint">No spam. Only a launch notification email.</p>
                    </form>
                )}
            </div>
        </div>
    );
}

export default WaitlistModal;
